/**
 * What Home says when a period closes (SPEC.md §9.1) — pure, so the rules are
 * tested without a screen.
 *
 * Two moments, never both on one open:
 *
 * 1. **The tide report**, on the first open after the close: what the period
 *    came to, how many categories stayed under their mark, which went over,
 *    and the one comparison against the period before it.
 * 2. **The swell**, on the next open, and only if the period ended above water:
 *    one line, "<Month> ended above water." Once, then still.
 *
 * A period with nothing in it — no spend and no budget, the state of anyone
 * who started this period — gets neither. There is nothing to report.
 */

import { type Cents, cents } from "@/domain/money";

export type PeriodEndShow = "report" | "swell" | null;

interface ClosedPeriod {
  spent: Cents;
  hasAnyBudget: boolean;
  remaining: Cents | null;
}

/** Whether a closed period has anything worth a report. */
export function hasHistory(closed: ClosedPeriod): boolean {
  return closed.spent > 0 || closed.hasAnyBudget;
}

/** Ended above water: a budget existed and the spend stayed at or under it. */
export function endedAbove(closed: ClosedPeriod): boolean {
  return closed.hasAnyBudget && closed.remaining !== null && closed.remaining >= 0;
}

/**
 * Which moment this open shows. Called **once per open**, with what has been
 * seen as of that open — so dismissing the report cannot bring the swell up
 * behind it on the same visit.
 */
export function periodEndMoment(
  closed: ClosedPeriod,
  seen: { report: boolean; swell: boolean },
): PeriodEndShow {
  if (!hasHistory(closed)) return null;
  if (!seen.report) return "report";
  if (endedAbove(closed) && !seen.swell) return "swell";
  return null;
}

interface ReportStanding {
  category: { id: string; name: string };
  budget: Cents | null;
  spent: Cents;
  remaining: Cents | null;
  isOver: boolean;
}

export interface TideReport {
  spent: Cents;
  budget: Cents | null;
  /** budget − spent; negative is over. Null without a budget. */
  left: Cents | null;
  held: boolean;
  /** Budgeted categories that stayed at or under their mark, and how many had one. */
  under: number;
  budgeted: number;
  /** Categories that went over, most over first. */
  over: { name: string; by: Cents }[];
  /** Spent in the closed period minus the one before it; null when that one was empty. */
  againstBefore: Cents | null;
}

export function tideReport(
  closed: ClosedPeriod & { totalBudget: Cents | null; standings: readonly ReportStanding[] },
  before: { spent: Cents },
): TideReport {
  const budgeted = closed.standings.filter((s) => s.budget !== null);
  const over = budgeted
    .filter((s) => s.isOver && s.remaining !== null)
    .map((s) => ({ name: s.category.name, by: cents(0 - (s.remaining ?? 0)) }))
    .sort((a, b) => b.by - a.by);
  return {
    spent: closed.spent,
    budget: closed.totalBudget,
    left: closed.remaining,
    held: endedAbove(closed),
    under: budgeted.length - over.length,
    budgeted: budgeted.length,
    over,
    againstBefore: before.spent > 0 ? cents(closed.spent - before.spent) : null,
  };
}

/** "September 2026" → "September". A ranged label ("25 Aug – 24 Sep") is left whole. */
export function periodName(label: string): string {
  const month = /^(\S+) \d{4}$/.exec(label);
  return month?.[1] ?? label;
}
